import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import type { Movie } from '@/lib/movieTypes';

interface HeroCarouselProps {
  movies: Movie[];
}

export default function HeroCarousel({ movies }: HeroCarouselProps) {
  const [index, setIndex] = useState(0);

  useEffect(() => {
    if (movies.length <= 1) return;
    const timer = setInterval(() => {
      setIndex((prev) => (prev + 1) % movies.length);
    }, 5000);
    return () => clearInterval(timer);
  }, [movies.length]);

  if (movies.length === 0) return null;
  const current = movies[index];

  return (
    <section className="relative w-full h-[440px] md:h-[600px] overflow-hidden bg-primary-900">
      <div className="absolute inset-0">
        {movies.map((movie, i) => (
          <div
            key={movie.id}
            className={`absolute inset-0 transition-opacity duration-700 ${
              i === index ? 'opacity-100' : 'opacity-0'
            }`}
          >
            <img
              src={movie.banner || movie.cover}
              alt={`${movie.title} 焦点推荐`}
              title={`${movie.title} 国语影音资源`}
              className="w-full h-full object-cover object-top"
            />
          </div>
        ))}
        <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/40 to-black/30"></div>
      </div>

      <div className="relative z-10 h-full w-full max-w-[1280px] mx-auto px-4 md:px-8 flex flex-col justify-end pb-10 md:pb-16">
        <div key={current.id} className="max-w-2xl animate-fade-up">
          <div className="flex items-center gap-2 mb-3">
            <span className="px-2.5 py-1 rounded-md bg-accent-500 text-background-50 text-xs font-semibold">
              {current.subCategoryName}
            </span>
            <span className="px-2.5 py-1 rounded-md bg-white/15 text-white text-xs backdrop-blur-sm">
              {current.typeName}
            </span>
          </div>
          <h1 className="text-3xl md:text-5xl font-bold text-white">{current.title}</h1>
          <div className="mt-3 flex items-center flex-wrap gap-x-4 gap-y-1 text-sm text-white/80">
            <span className="flex items-center gap-1 text-accent-400 font-semibold">
              <i className="ri-star-fill"></i>
              {current.rating.toFixed(1)}
            </span>
            <span>{current.year}</span>
            <span>{current.region}</span>
            <span>{current.duration}</span>
            <span className="hidden sm:inline">{current.director} 导演</span>
          </div>
          <p className="mt-3 text-sm md:text-base text-white/80 leading-relaxed clamp-2 max-w-xl">
            {current.summary}
          </p>
          <div className="mt-5 flex flex-wrap items-center gap-3">
            <Link
              to={`/detail/${current.id}`}
              className="inline-flex items-center gap-1.5 h-11 px-6 rounded-md bg-accent-500 text-background-50 text-sm font-medium whitespace-nowrap hover:bg-accent-600 transition-colors"
            >
              <i className="ri-play-circle-line text-lg"></i>
              查看详情
            </Link>
            <Link
              to={`/category/${current.type}`}
              className="inline-flex items-center gap-1.5 h-11 px-6 rounded-md border border-white/40 text-white text-sm font-medium whitespace-nowrap hover:bg-white/10 transition-colors"
            >
              同类资源
            </Link>
          </div>
        </div>

        <div className="absolute bottom-10 md:bottom-16 right-4 md:right-8 flex items-center gap-2">
          {movies.map((movie, i) => (
            <button
              key={movie.id}
              type="button"
              aria-label={`切换到 ${movie.title}`}
              onClick={() => setIndex(i)}
              className={`h-1.5 rounded-full transition-all cursor-pointer ${
                i === index ? 'w-8 bg-accent-500' : 'w-4 bg-white/40 hover:bg-white/70'
              }`}
            />
          ))}
        </div>
      </div>

      <button
        type="button"
        aria-label="上一个"
        onClick={() => setIndex((prev) => (prev - 1 + movies.length) % movies.length)}
        className="hidden md:flex absolute left-4 top-1/2 -translate-y-1/2 w-10 h-10 items-center justify-center rounded-full bg-black/30 text-white hover:bg-black/50 transition-colors cursor-pointer"
      >
        <i className="ri-arrow-left-s-line text-2xl"></i>
      </button>
      <button
        type="button"
        aria-label="下一个"
        onClick={() => setIndex((prev) => (prev + 1) % movies.length)}
        className="hidden md:flex absolute right-4 top-1/2 -translate-y-1/2 w-10 h-10 items-center justify-center rounded-full bg-black/30 text-white hover:bg-black/50 transition-colors cursor-pointer"
      >
        <i className="ri-arrow-right-s-line text-2xl"></i>
      </button>
    </section>
  );
}